import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { api, setAccessToken } from "@/lib/api";
import { canVoidInvoice as roleCanVoidInvoice, isSalonAdmin as roleIsSalonAdmin } from "@/lib/roles";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Effective pages this user can reach inside their tenant. Loaded from
  // GET /api/me/subscription, refreshed each time the user changes (login
  // / logout). Defaulting to `null` means "not loaded yet" and the helper
  // below falls back to allow-all so the UI doesn't briefly hide menu
  // items on first paint.
  const [effectivePages, setEffectivePages] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      // Token is stale/missing — clear it so we don't keep sending a
      // dead Bearer header on subsequent requests.
      setAccessToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: Skip /me check if returning from OAuth callback
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    const onPageShow = (e) => {
      // Browser back/forward cache can restore stale UI. Force auth refresh
      // when a persisted page is restored.
      if (e.persisted) checkAuth();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [checkAuth]);

  // Pull the tenant's plan-derived effective pages whenever a user session
  // becomes active. Refresh on demand (window.refreshPlanPages) so a plan
  // edit from another tab can repaint the sidebar without a full reload.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!user) { setEffectivePages(null); return; }
      try {
        const r = await api.get("/me/subscription");
        if (!cancelled) setEffectivePages(r.data?.effective_pages || null);
      } catch { if (!cancelled) setEffectivePages(null); }
    };
    load();
    window.refreshPlanPages = load;  // expose for cross-tab refresh
    return () => { cancelled = true; delete window.refreshPlanPages; };
  }, [user]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    // Clear bearer fallback first, then verify backend session state.
    setAccessToken(null);
    setUser(null);
    try {
      await api.get(`/auth/me?ts=${Date.now()}`);
      // Backend still sees an active session; force login page and trigger a
      // fresh auth check there.
      window.location.replace("/login?logout=retry");
    } catch {
      // 401 expected after successful logout.
      window.location.replace("/login");
    }
  };

  // Permission helpers — single source of truth shared by Layout/Routes/Dashboard.
  const helpers = useMemo(() => {
    const role = user?.role || null;
    const allowed = user?.allowed_modules || [];
    const branchIds = user?.branch_ids || [];
    const isAdmin = role === "admin";
    const isSalonAdmin = roleIsSalonAdmin(role);
    const canVoidInvoice = roleCanVoidInvoice(role);
    const hasWildcard = allowed.includes("*");

    const canAccess = (moduleId) => {
      if (!moduleId) return true;
      if (isAdmin || hasWildcard) return true;
      // No restrictions = legacy behaviour: full access.
      // We only enforce when the user explicitly has allowed_modules set.
      if (!allowed.length) return true;
      return allowed.includes(moduleId);
    };

    // Plan-level page gate. Returns true if either:
    //  • pages haven't loaded yet (avoid flicker on first paint), OR
    //  • the page is in the effective list returned by the backend.
    // The backend is still the authoritative gate; this is the UI optimisation.
    const canAccessPage = (pageId) => {
      if (!pageId) return true;
      if (isAdmin) return true;                  // super-admin sees all
      if (effectivePages === null) return true;  // not loaded yet — don't flicker
      return effectivePages.includes(pageId);
    };

    const canViewExecutive = isAdmin || hasWildcard || allowed.includes("dashboard_executive") || !!user?.can_view_executive;

    const branchRestricted = !isAdmin && !isSalonAdmin && branchIds.length > 0;
    return { isAdmin, isSalonAdmin, canVoidInvoice, canAccess, canAccessPage, canViewExecutive, branchIds, branchRestricted, effectivePages };
  }, [user, effectivePages]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout, ...helpers }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside provider");
  return ctx;
};
